import type { DailyStatusTodayDto } from "@/features/daily/types/daily.types";
import { commandViewModel, dailyFormat } from "@/features/daily/components/command/command-view-model";
import { QueueRowVm, diagnosticViewModel } from "@/features/daily/components/diagnostic/diagnostic-view-model";
import type { DailyQueuePageDto } from "@/features/daily/types/daily.types";

type PrioritySource = DailyStatusTodayDto["priorityOperations"][number]["source"];
type BaselineStatus = DailyStatusTodayDto["hourlyToday"]["baseline"];

export type TodayHourVm = {
    hour: number;
    label: string;
    created: number;
    /** B18 (D136): the created split by type — the stacked bars. */
    createdOrders: number;
    createdDeposits: number;
    completed: number;
    /** The baseline's expected creations for that hour; `null` without a baseline. */
    expected: number | null;
    quiet: boolean;
};

export type TodayHourlyVm = {
    hours: TodayHourVm[];
    /** D139: stack by type only when both orders and deposits were created today. */
    byType: boolean;
    /** Why no expected line is drawn; `null` when the baseline answered. */
    baselineNote: string | null;
};

export type TodayFunnelStepVm = {
    key: "created" | "funded" | "completed";
    label: string;
    countLabel: string;
    /** Share of the day's created orders, 0–1; `null` when nothing was created. */
    share: number | null;
    shareLabel: string;
};

export type TodayTypeFunnelVm = {
    key: "order" | "deposit";
    title: string;
    steps: TodayFunnelStepVm[];
    /** D146: the conversion itself — final for today; «—» with its reason below the minimum sample. */
    headlineLabel: string;
    headlineValue: string;
    tooltip: string[];
    /** D145: «+1 completada hoy que se creó antes» — what reconciles the funnel with the tile; `null` at 0. */
    createdBeforeNote: string | null;
};

/** D136: one funnel per type — orders and deposits are never averaged together. */
export type TodayFunnelVm = TodayTypeFunnelVm[];

export type PriorityRowVm = {
    /** Why it is a priority; `null` for rows read from the priority queue page (D144). */
    sourceLabel: string | null;
    row: QueueRowVm;
};

export const CONVERSION_DEFINITION = "Conversión: operaciones con fondos recibidos sobre creadas · sin canceladas";

const BASELINE_NOTE: Record<BaselineStatus, string | null> = {
    ok: null,
    insufficient: "Sin semanas suficientes para la línea esperada",
    unavailable: "Línea esperada no disponible",
};

const SOURCE_TEXT: Record<PrioritySource, string> = {
    overdue: "Fuera de plazo",
    credit_stalled: "Crédito detenido",
    reviewing_funded: "En revisión con fondos",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** D24: the cache stores `unknown`; the page proves the shape instead of casting. */
export const isStatusTodayDto = (value: unknown): value is DailyStatusTodayDto =>
    isRecord(value) && isRecord(value.envelope) && isRecord(value.hourlyToday) && isRecord(value.todayFunnel) && Array.isArray(value.priorityOperations);

const createdBeforeNote = (count: number, singular: string, plural: string): string | null =>
    count === 0 ? null : count === 1 ? `+1 ${singular} hoy que se creó antes` : `+${dailyFormat.exactCount(count)} ${plural} hoy que se crearon antes`;

const shareOf = (count: number, created: number): number | null => (created === 0 ? null : count / created);

export const todayViewModel = {
    /** D134: created (bars), completed (line) and the baseline's expected creations, hour by hour. */
    hourly: (dto: DailyStatusTodayDto): TodayHourlyVm => ({
        hours: dto.hourlyToday.hours.map((hour) => ({
            hour: hour.hour,
            label: `${String(hour.hour).padStart(2, "0")}h`,
            created: hour.created,
            createdOrders: hour.createdByType.order,
            createdDeposits: hour.createdByType.deposit,
            completed: hour.completed,
            expected: hour.expectedCreated,
            quiet: hour.quiet === true,
        })),
        baselineNote: BASELINE_NOTE[dto.hourlyToday.baseline],
        byType: dto.hourlyToday.hours.some((hour) => hour.createdByType.order !== 0)
            && dto.hourlyToday.hours.some((hour) => hour.createdByType.deposit !== 0),
    }),

    /**
     * D134: created → with funds → completed; each step as a share of the created. D146:
     * the conversion has no maturation any more, so today's figure is final and headlines
     * each funnel (the server flags it `insufficient` below the minimum sample).
     */
    funnel: (dto: DailyStatusTodayDto): TodayFunnelVm => {
        const { order, deposit } = dto.todayFunnel;
        const step = (key: TodayFunnelStepVm["key"], label: string, count: number, created: number): TodayFunnelStepVm => {
            const share = shareOf(count, created);
            return { key, label, countLabel: dailyFormat.exactCount(count), share, shareLabel: dailyFormat.kpiPercent(share, 1) };
        };
        const funnels: TodayFunnelVm = [
            {
                key: "order",
                title: "Órdenes",
                steps: [
                    step("created", "Creadas", order.created, order.created),
                    step("funded", "Con fondos", order.funded, order.created),
                    step("completed", "Completadas", order.completed, order.created),
                ],
                headlineLabel: "Conversión",
                headlineValue: dailyFormat.kpiPercent(order.conversion.value, 1),
                tooltip: [CONVERSION_DEFINITION, ...commandViewModel.reasonTexts(order.conversion.reasonCodes)],
                createdBeforeNote: createdBeforeNote(order.completedTodayCreatedBefore, "completada", "completadas"),
            },
            {
                key: "deposit",
                title: "Depósitos",
                steps: [
                    step("created", "Creados", deposit.created, deposit.created),
                    step("funded", "Recibidos", deposit.received, deposit.created),
                    step("completed", "Acreditados", deposit.credited, deposit.created),
                ],
                headlineLabel: "Conversión",
                headlineValue: dailyFormat.kpiPercent(deposit.conversion.value, 1),
                tooltip: ["Conversión: depósitos con el dinero recibido sobre los creados · sin cancelados", ...commandViewModel.reasonTexts(deposit.conversion.reasonCodes)],
                createdBeforeNote: createdBeforeNote(deposit.completedTodayCreatedBefore, "acreditado", "acreditados"),
            },
        ];
        // D139: a type with nothing created today is not drawn beside one that has activity.
        const active = funnels.filter((funnel) => (funnel.key === "order" ? order.created : deposit.created) !== 0);
        return active.length === 0 ? funnels : active;
    },

    /** D144: the first page of `queue=priority`, filling the panel once it arrives. */
    priorityPage: (page: DailyQueuePageDto): PriorityRowVm[] =>
        page.rows.map((row) => ({ sourceLabel: null, row: diagnosticViewModel.row(row, page.envelope.evaluatedAt) })),

    /** D134: the operations to look at first, in the server's order. */
    priority: (dto: DailyStatusTodayDto): PriorityRowVm[] =>
        dto.priorityOperations.map((operation) => ({
            sourceLabel: SOURCE_TEXT[operation.source],
            row: diagnosticViewModel.row(operation.row, dto.envelope.evaluatedAt),
        })),
};
